import { ChangeEvent, FC } from 'react';

export interface DatePickerProps {
  /** Current value as Date object or ISO string */
  value?: Date | string;
  /** Change handler */
  onChange: (date: Date) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Error message */
  error?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Minimum date */
  min?: Date | string;
  /** Maximum date */
  max?: Date | string;
  /** Whether to show time picker */
  showTime?: boolean;
}

/**
 * DatePicker Component
 * 
 * A reusable date/datetime picker component using HTML5 input
 * Supports both date-only and datetime-local modes
 */
export const DatePicker: FC<DatePickerProps> = ({
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  placeholder,
  className = '',
  min,
  max,
  showTime = true,
}) => {
  // Convert value to appropriate format for input
  const formatValue = (val?: Date | string): string => {
    if (!val) return '';
    
    const dateObj = val instanceof Date ? val : new Date(val);
    
    // Check if valid date
    if (isNaN(dateObj.getTime())) return '';
    
    // Format for datetime-local or date input
    if (showTime) {
      // Format: YYYY-MM-DDTHH:mm (use local time)
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } else {
      // Format: YYYY-MM-DD (use local date, not UTC)
      // This prevents timezone conversion from changing the displayed date
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  };

  // Convert min/max to appropriate format
  const formatMinMax = (val?: Date | string): string | undefined => {
    if (!val) return undefined;
    const dateObj = val instanceof Date ? val : new Date(val);
    if (isNaN(dateObj.getTime())) return undefined;
    
    if (showTime) {
      // Format: YYYY-MM-DDTHH:mm (use local time)
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } else {
      // Format: YYYY-MM-DD (use local date, not UTC)
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      onChange(new Date());
      return;
    }
    
    const newDate = new Date(inputValue);
    if (!isNaN(newDate.getTime())) {
      onChange(newDate);
    }
  };

  const inputType = showTime ? 'datetime-local' : 'date';
  const formattedValue = formatValue(value);
  const formattedMin = formatMinMax(min);
  const formattedMax = formatMinMax(max);

  return (
    <div className="w-full">
      <input
        type={inputType}
        value={formattedValue}
        onChange={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={placeholder}
        min={formattedMin}
        max={formattedMax}
        className={`
          w-full px-3 py-2 border rounded-md
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          disabled:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60
          transition-colors duration-150
          ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'}
          ${className}
        `}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600 font-medium">{error}</p>
      )}
    </div>
  );
};

export default DatePicker;

